// ============================================================
// RaiseBillModal.tsx — Invoicing Stage 1 (mockup screen 2 "Raise a draft
// from WIP"). Everything unbilled is PRE-TICKED: time grouped by month (6-min
// units at the frozen rate, as the server computed them) + PAID disbursements
// (each with its VAT treatment); anticipated disbursements are shown but held
// back. Bill type / time format / untick → Create draft. The running total
// sums the SERVER's per-line figures (net + per-line VAT) for display; the
// draft itself is recomputed server-side on create (nothing priced here).
// An on-account bill is a single sum (no WIP consumed).
// ============================================================
import { ErrorNote } from '../shared/ActionBox'
import React, { useMemo, useState } from 'react'
import ModalFrame, { HERO_PILL, HERO_PILL_SOFT } from '../shared/ModalFrame'
import { createBillApi } from '../../api/casevoApi'
import type { Bill, BillType, CaseBilling, CostsFormat, VatTreatment } from '../../types/billing.types'
import {
  BIcon, B_PRIMARY_LG, B_SECONDARY, CARD, EYEBROW, INPUT, LABEL, NOTE, TotRow, BalanceBand,
  MoneyInput, TypePill, VatChip, YOURS_ONLY_TIP, errorText, fmtDuration, money, parsePounds, partialMoney,
} from './billingUi'
import WithheldAmount from '../shared/WithheldAmount'
import UserAvatar from '../shared/UserAvatar'
import { useDraftDirty, useUnsavedChangesGuard } from '../../hooks/useUnsavedChangesGuard'

interface Props {
  caseName: string
  caseRef: string
  caseId: string
  data: CaseBilling
  onClose: () => void
  onCreated: (bill: Bill) => void
}

const Check: React.FC<{ state: 'on' | 'off' | 'part' | 'dis'; onClick?: () => void; label: string }> = ({ state, onClick, label }) => (
  <button type="button" role="checkbox" aria-checked={state === 'on' ? true : state === 'part' ? 'mixed' : false} aria-label={label}
    disabled={state === 'dis'} onClick={onClick}
    className={`w-[18px] h-[18px] rounded-[5px] border-[1.5px] grid place-items-center shrink-0 focus:outline-none casevo-focus ${state === 'on' || state === 'part' ? 'bg-casevo-primary border-casevo-primary text-white' : state === 'dis' ? 'border-casevo-border bg-casevo-input opacity-45' : 'border-casevo-border bg-casevo-surface'}`}>
    {state === 'on' && <BIcon name="check" size={11} strokeWidth={3} />}
    {state === 'part' && <span className="w-[8px] h-[2px] bg-white rounded" />}
  </button>
)

const Seg: React.FC<{ value: string; options: [string, string][]; onChange: (v: string) => void; label: string }> = ({ value, options, onChange, label }) => (
  <div role="radiogroup" aria-label={label} className="inline-flex self-start border border-casevo-border rounded-[10px] overflow-hidden bg-casevo-surface">
    {options.map(([k, l], i) => (
      <button key={k} type="button" role="radio" aria-checked={value === k} onClick={() => onChange(k)}
        className={`px-[13px] py-[7px] text-[13px] inline-flex items-center gap-[6px] whitespace-nowrap focus:outline-none casevo-focus ${i ? 'border-l border-casevo-border' : ''} ${value === k ? 'bg-casevo-primary text-white font-semibold' : 'text-casevo-mid hover:bg-casevo-mint/30'}`}>
        {value === k && <BIcon name="check" size={11} />}{l}
      </button>
    ))}
  </div>
)

const RaiseBillModal: React.FC<Props> = ({ caseName, caseRef, caseId, data, onClose, onCreated }) => {
  const [billType, setBillType] = useState<BillType>('INTERIM_STATUTE')
  const [format, setFormat] = useState<CostsFormat>('ITEMISED')
  const [offT, setOffT] = useState<Set<string>>(() => new Set())
  const readyDisb = useMemo(() => data.disbursements.filter((d) => d.status === 'PAID' && !d.invoiceId), [data])
  const anticipated = useMemo(() => data.disbursements.filter((d) => d.status === 'ANTICIPATED'), [data])
  const [offD, setOffD] = useState<Set<string>>(() => new Set())
  const [oaAmount, setOaAmount] = useState('')
  const [oaDesc, setOaDesc] = useState('Payment on account of costs and disbursements')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  // Unsaved-changes guard: ×, Esc, backdrop, Cancel (and leaving the screen /
  // Back / refresh) ask before dropping what was typed here.
  const guardClose = useUnsavedChangesGuard(useDraftDirty(true, [billType, format, Array.from(offT), Array.from(offD), oaAmount, oaDesc]) && !busy)
  const requestClose = (): void => guardClose(onClose)
  const onAccount = billType === 'ON_ACCOUNT'

  // Group the WIP by month (server order = oldest first).
  const months = useMemo(() => {
    const m: { key: string; label: string; ids: string[] }[] = []
    for (const w of data.wip) {
      const d = new Date(w.date)
      const key = `${d.getFullYear()}-${d.getMonth()}`
      let g = m.find((x) => x.key === key)
      if (!g) { g = { key, label: d.toLocaleDateString('en-GB', { month: 'long', year: 'numeric' }), ids: [] }; m.push(g) }
      g.ids.push(w.id)
    }
    return m
  }, [data.wip])
  const wipById = useMemo(() => new Map(data.wip.map((w) => [w.id, w])), [data.wip])

  const selT = data.wip.filter((w) => !offT.has(w.id))
  const selD = readyDisb.filter((d) => !offD.has(d.id))
  const disbVat = (t: VatTreatment, v: number) => (t === 'NONE' ? 0 : v)
  // A colleague's entry may come back with its £ withheld (null): sums cover
  // the viewer's own entries only, labelled "(yours)" — never a fake total.
  const pc = selT.reduce((s, w) => s + (w.amountPence ?? 0), 0)
  const pcVat = selT.reduce((s, w) => s + (w.vatPence ?? 0), 0)
  const selWithheld = selT.some((w) => w.amountPence == null)
  const units = selT.reduce((s, w) => s + w.units, 0)
  const dn = selD.reduce((s, d) => s + d.amountPence, 0)
  const dv = selD.reduce((s, d) => s + disbVat(d.vatTreatment, d.vatPence), 0)
  const total = pc + dn + pcVat + dv
  // null → the money held on account is hidden from you (a bill with colleagues'
  // billing drew on it): no credit preview; the server still credits it at issue.
  const oaHidden = data.onAccountAvailablePence == null
  const credit = Math.min(data.onAccountAvailablePence ?? 0, Math.max(0, total))
  const oaPence = parsePounds(oaAmount)
  const periodDates = selT.map((w) => new Date(w.date).getTime())
  const period = periodDates.length ? `${new Date(Math.min(...periodDates)).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })} → ${new Date(Math.max(...periodDates)).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })}` : '—'

  const toggle = (set: Set<string>, setter: (s: Set<string>) => void, ids: string[], on: boolean) => {
    const n = new Set(set); ids.forEach((id) => (on ? n.delete(id) : n.add(id))); setter(n)
  }
  const groupState = (ids: string[], off: Set<string>): 'on' | 'off' | 'part' => {
    const offCount = ids.filter((id) => off.has(id)).length
    return offCount === 0 ? 'on' : offCount === ids.length ? 'off' : 'part'
  }

  const canCreate = onAccount ? oaPence != null && oaPence > 0 : selT.length + selD.length > 0
  const create = async (): Promise<void> => {
    if (!canCreate || busy) return
    setBusy(true); setError(null)
    try {
      const bill = await createBillApi(caseId, onAccount
        ? { billType, costsFormat: format, amountPence: oaPence as number, description: oaDesc }
        : { billType, costsFormat: format, activityIds: selT.map((w) => w.id), disbursementIds: selD.map((d) => d.id) })
      onCreated(bill)
    } catch (e) { setError(errorText(e, 'Couldn’t create the draft bill.')) } finally { setBusy(false) }
  }

  return (
    <ModalFrame
      onClose={requestClose} size="xxl" portal scrollBody
      eyebrow="New bill" eyebrowIcon={<BIcon name="bill" size={14} />}
      pills={<><span className={HERO_PILL_SOFT}>{caseRef}</span><span className={`${HERO_PILL} !bg-white/10`}>From unbilled WIP</span></>}
      title={`Bill ${caseName}`}
      subtitle={onAccount ? 'Ask the client for a sum on account. Nothing is taken from WIP.' : `Everything unbilled up to today is already selected — untick anything you’re not billing yet.`}
      bodyPadding={false}
      footerLeft={<span className={`${NOTE} inline-flex items-center gap-[7px]`}><BIcon name="lock" size={13} />Nothing is sent or numbered yet. You’ll check the draft next.</span>}
      footerRight={<>
        <button type="button" className={B_SECONDARY} onClick={requestClose}>Cancel</button>
        <button type="button" className={B_PRIMARY_LG} disabled={!canCreate || busy} onClick={() => { void create() }}>
          {busy ? 'Creating…' : 'Create draft bill'}<BIcon name="chevr" size={13} />
        </button>
      </>}
    >
      <div className="px-7 pt-[18px] pb-[6px] grid grid-cols-1 md:grid-cols-3 gap-4 border-b border-casevo-border-soft">
        <div className="flex flex-col gap-[5px]">
          <span className={LABEL}>Bill type</span>
          <Seg label="Bill type" value={billType} onChange={(v) => setBillType(v as BillType)} options={[['ON_ACCOUNT', 'On account'], ['INTERIM_STATUTE', 'Interim statute'], ['FINAL_STATUTE', 'Final statute']]} />
          <span className="text-[11.5px] text-casevo-text-soft">
            {billType === 'FINAL_STATUTE' ? 'Final bill — the client can ask for it to be assessed by the court.' : billType === 'INTERIM_STATUTE' ? 'Interim statute bill — a complete bill for the period, assessable on its own.' : 'A request for money on account — credited on the next statute bill once paid.'}
          </span>
        </div>
        {!onAccount && <>
          <div className="flex flex-col gap-[5px]">
            <span className={LABEL}>Period</span>
            <div className={`${INPUT} flex items-center gap-2 tabular-nums`}><BIcon name="cal" size={13} />{period}</div>
            <span className="text-[11.5px] text-casevo-text-soft">Set from the first and last entry you’ve ticked</span>
          </div>
          <div className="flex flex-col gap-[5px]">
            <span className={LABEL}>Show time on the bill as</span>
            <Seg label="Time format" value={format} onChange={(v) => setFormat(v as CostsFormat)} options={[['ITEMISED', 'Itemised'], ['BY_FEE_EARNER', 'By fee earner'], ['SINGLE_LINE', 'Single line']]} />
            <span className="text-[11.5px] text-casevo-text-soft">You can change this on the next screen</span>
          </div>
        </>}
      </div>

      {onAccount ? (
        <div className="px-7 py-5 grid grid-cols-1 md:grid-cols-[1fr_220px] gap-4 max-w-[720px]">
          <label><span className={LABEL}>Description</span><input className={INPUT} value={oaDesc} onChange={(e) => setOaDesc(e.target.value)} maxLength={500} /></label>
          <label><span className={LABEL}>Amount</span><MoneyInput value={oaAmount} onChange={setOaAmount} ariaLabel="Amount on account"
            error={oaAmount !== '' && (oaPence == null || oaPence <= 0) ? 'Enter an amount like 500 or 500.00.' : null} /></label>
        </div>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-[1fr_310px] gap-[22px] px-7 pt-4 pb-[22px]">
          <div className="min-w-0">
            <div className="flex items-center gap-[10px] flex-wrap">
              <Check label="All time" state={data.wip.length ? groupState(data.wip.map((w) => w.id), offT) : 'dis'} onClick={() => toggle(offT, setOffT, data.wip.map((w) => w.id), groupState(data.wip.map((w) => w.id), offT) !== 'on')} />
              <b className="text-[14.5px] text-casevo-forest">Time</b>
              <span className={NOTE}>{selT.length} {selT.length === 1 ? 'entry' : 'entries'} · {units} units · {partialMoney(pc, selWithheld)}</span>
              <span className={`${NOTE} ml-auto`}>Documents and other non-billable entries aren’t listed</span>
            </div>
            {data.wip.length === 0 && <p className={`${NOTE} mt-3`}>No unbilled time on this case.</p>}
            {months.map((g) => {
              const st = groupState(g.ids, offT)
              const gSel = g.ids.filter((id) => !offT.has(id)).map((id) => wipById.get(id)!)
              return (
                <div key={g.key}>
                  <div className="grid grid-cols-[22px_1fr_auto] gap-[10px] items-center px-[6px] pt-3 pb-[6px]">
                    <Check label={`All of ${g.label}`} state={st} onClick={() => toggle(offT, setOffT, g.ids, st !== 'on')} />
                    <span className="text-[11px] font-bold tracking-[0.14em] uppercase text-casevo-text-soft">{g.label}</span>
                    <span className={`${NOTE} tabular-nums`}>{gSel.reduce((s, w) => s + w.units, 0)} units · <b className="text-casevo-forest">{partialMoney(gSel.reduce((s, w) => s + (w.amountPence ?? 0), 0), gSel.some((w) => w.amountPence == null))}</b></span>
                  </div>
                  {g.ids.map((id) => {
                    const w = wipById.get(id)!
                    const on = !offT.has(id)
                    return (
                      <div key={id} className={`grid grid-cols-[22px_70px_78px_1fr_26px_92px_80px] gap-[10px] items-center px-[6px] py-[9px] border-t border-casevo-border-soft text-[13.5px] ${on ? '' : 'opacity-55'}`}>
                        <Check label={w.description} state={on ? 'on' : 'off'} onClick={() => toggle(offT, setOffT, [id], !on)} />
                        <span className="text-[12.5px] text-casevo-text-soft tabular-nums">{new Date(w.date).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' })}</span>
                        <span className="justify-self-start"><TypePill type={w.actionType} /></span>
                        <span className="min-w-0 truncate text-casevo-forest" title={w.description}>{w.description}{w.onDraft && <span className="ml-2 text-[11px] text-[#8a6212] dark:text-[#dcbb72]">on a draft</span>}</span>
                        <UserAvatar userId={w.feeEarnerId} fallbackName={w.feeEarnerName} />
                        <span className="text-right text-[12.5px] text-casevo-text-soft tabular-nums whitespace-nowrap">{fmtDuration(w.durationSeconds)} · <b className="text-casevo-forest font-semibold">{w.units}u</b></span>
                        <span className="text-right font-semibold tabular-nums text-casevo-forest">{w.amountPence == null ? <WithheldAmount /> : money(w.amountPence)}</span>
                      </div>
                    )
                  })}
                </div>
              )
            })}

            <div className="flex items-center gap-[10px] mt-[18px]">
              <Check label="All disbursements" state={readyDisb.length ? groupState(readyDisb.map((d) => d.id), offD) : 'dis'} onClick={() => toggle(offD, setOffD, readyDisb.map((d) => d.id), groupState(readyDisb.map((d) => d.id), offD) !== 'on')} />
              <b className="text-[14.5px] text-casevo-forest">Disbursements</b>
              <span className={NOTE}>{selD.length} paid · {money(dn)} + VAT {money(dv)}</span>
            </div>
            <div className="mt-[6px]">
              {readyDisb.map((d) => {
                const on = !offD.has(d.id)
                return (
                  <div key={d.id} className={`grid grid-cols-[22px_1fr_auto_90px] gap-[10px] items-center px-[6px] py-[10px] border-t border-casevo-border-soft text-[13.5px] ${on ? '' : 'opacity-55'}`}>
                    <Check label={d.description} state={on ? 'on' : 'off'} onClick={() => toggle(offD, setOffD, [d.id], !on)} />
                    <span className="min-w-0"><span className="block font-medium truncate text-casevo-forest">{d.description}</span><span className={NOTE}>{d.payableTo}</span></span>
                    <VatChip t={d.vatTreatment} />
                    <span className="text-right font-semibold tabular-nums text-casevo-forest">{money(d.amountPence)}</span>
                  </div>
                )
              })}
              {anticipated.map((d) => (
                <div key={d.id} className="grid grid-cols-[22px_1fr_auto_90px] gap-[10px] items-center px-[6px] py-[10px] border-t border-casevo-border-soft text-[13.5px] opacity-60">
                  <Check label={d.description} state="dis" />
                  <span className="min-w-0"><span className="block font-medium truncate text-casevo-forest">{d.description}</span><span className={NOTE}>Anticipated · can be billed once it’s paid</span></span>
                  <VatChip t={d.vatTreatment} />
                  <span className="text-right tabular-nums text-casevo-forest">{money(d.amountPence)}</span>
                </div>
              ))}
              {readyDisb.length + anticipated.length === 0 && <p className={`${NOTE} mt-2`}>No disbursements recorded on this case.</p>}
            </div>
          </div>

          <div className="lg:sticky lg:top-0 self-start">
            <div className={`${CARD} p-[18px]`}>
              <div className={EYEBROW}>Draft total</div>
              <TotRow k={<>Profit costs <span className="text-[12px]">· {units} units</span></>} v={<>{partialMoney(pc, selWithheld)}</>} />
              <TotRow k="Disbursements" v={money(dn)} />
              <TotRow k="VAT" v={money(pcVat + dv)} />
              <TotRow sub k="20% on profit costs" v={<>{partialMoney(pcVat, selWithheld)}</>} />
              <TotRow sub k="on disbursements" v={money(dv)} />
              <TotRow big k="Total" v={<>{partialMoney(total, selWithheld)}</>} />
              {credit > 0 && <TotRow k="Less paid on account" v={`−${money(credit)}`} />}
              {/* Colleagues' entries selected (£ withheld): no balance figure — over only
                  your items it would read as "the amount due". The bill carries them in full. */}
              <BalanceBand pence={selWithheld ? null : total - credit} hiddenNote="Partial — some items hidden" trailing={<BIcon name="check" size={18} />} />
              {oaHidden && <p className={`${NOTE} mt-2 mb-0 flex items-start gap-[5px]`}><WithheldAmount size={11} />Any money held on account is credited when the bill is issued — how much is hidden from you, because a bill with colleagues’ billing drew on it.</p>}
              {selWithheld && <p className={`${NOTE} mt-2 mb-0`}>{YOURS_ONLY_TIP} — the bill itself carries every selected entry at its full value.</p>}
              <div className={`${NOTE} mt-3 flex gap-[7px]`}><BIcon name="info" size={13} className="mt-[2px]" /><span>{selT.length} of {data.wip.length} time entries · {selD.length} of {readyDisb.length} paid disbursements. VAT is worked out line by line at 20%, at cost or none — and recalculated by the server when the draft is created.</span></div>
            </div>
            {credit > 0 && (
              <div className={`${CARD} px-4 py-3 mt-[10px] flex gap-[10px] items-center text-[13px] text-casevo-forest`}>
                <BIcon name="check" size={14} className="text-casevo-brand" />
                <span><b>{money(data.onAccountAvailablePence ?? 0)}</b> held on account will be credited when the bill is issued</span>
              </div>
            )}
          </div>
        </div>
      )}
      {error && <ErrorNote className="mx-7 mb-4">{error}</ErrorNote>}
    </ModalFrame>
  )
}

export default RaiseBillModal
